import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClientContainer, UserContainer } from '@/providers';
import { trackEvent } from '@/helpers/external-tools-helper';
import { DEMO_MODE_EVENTS } from '@/analytics/demo-mode';
import {
  DEMO_MODE_STORAGE_KEYS,
  demoModeStorage,
} from '@/utils/demo-mode-storage';
import { useAtomValue } from 'jotai';
import {
  demoProgressAtom,
  demoProgressCountAtom,
  demoProgressPercentAtom,
  isDemoProgressCompleteAtom,
} from '@/states/demo-progress';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { Confetti, ConfettiRef } from '@/components/ui/confetti';
import { BusinessSwitchLoader } from '@/components/ui/business-switch-loader';
import { useSwitchBusiness } from '@/hooks/use-business-switch';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

export const DemoSidebarCard: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const switchBusiness = useSwitchBusiness();

  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const progress = useAtomValue(demoProgressAtom);
  const progressCount = useAtomValue(demoProgressCountAtom);
  const progressPercent = useAtomValue(demoProgressPercentAtom);
  const isComplete = useAtomValue(isDemoProgressCompleteAtom);

  // Confetti state
  const confettiRef = useRef<ConfettiRef>(null);
  const [previousIsComplete, setPreviousIsComplete] = useState(isComplete);

  const triggerSideCannons = useCallback(() => {
    const colors = [
      '#FF3D7F',
      '#FF9A3D',
      '#FFE23D',
      '#3DFF7F',
      '#3DFFF5',
      '#3D7FFF',
      '#9A3DFF',
      '#FF3DD1',
      '#3DFF9A',
      '#FF573D',
    ];
    // This setTimeout is a workaround to fix the issue with confetti not being triggered programmatically
    setTimeout(() => {
      confettiRef.current?.fire({
        particleCount: 30,
        angle: 60,
        spread: 35,
        startVelocity: 30,
        origin: { x: 0, y: 1 },
        colors,
        scalar: 0.6,
      });

      confettiRef.current?.fire({
        particleCount: 30,
        angle: 120,
        spread: 35,
        startVelocity: 30,
        origin: { x: 1, y: 1 },
        colors,
        scalar: 0.6,
      });
    }, 100);
  }, [confettiRef]);

  // Trigger confetti when demo is completed
  useEffect(() => {
    if (isComplete && !previousIsComplete) {
      triggerSideCannons();
    }
    setPreviousIsComplete(isComplete);
  }, [isComplete, previousIsComplete, triggerSideCannons]);

  const handleExploreDashboard = () => {
    const clientId = clientState.selectedClient?.id;

    // Reset tour flags to restart the tour flow - allows tour to run even if previously completed/skipped
    try {
      localStorage.removeItem(DEMO_MODE_STORAGE_KEYS.HAS_SEEN_WELCOME);
      localStorage.removeItem(DEMO_MODE_STORAGE_KEYS.IS_TOUR_COMPLETED);
      // Ensure hasVisitedDemo is set so the tour flow works
      demoModeStorage.markDemoAsVisited();
    } catch (error) {
      // Error resetting tour flags, continue anyway
    }

    // Track event with sidebar source
    trackEvent(DEMO_MODE_EVENTS.DEMO_TOUR_STARTED, {
      source: 'sidebar',
      trigger: 'explore_analytics_button',
      clientId,
    });

    // Navigate to social media analytics page with state to directly start tour (skip welcome modal)
    if (clientId) {
      navigate(`/ws/${clientId}/analytics/social`, {
        state: { startTourDirectly: true },
      });
    }
  };

  const handleExitDemoMode = useCallback(async () => {
    try {
      setIsExiting(true);
      setShowExitConfirmation(false);

      // Find primary business using access.isPrimary flag
      const primaryBusiness = userState.businessOptions?.find(
        (business: any) => business.access.isPrimary
      );

      if (!primaryBusiness) {
        setIsExiting(false);
        toast({
          variant: 'destructive',
          title: t('demo-mode-primary-business-not-found'),
          description: t('demo-mode-unable-to-find-primary-business'),
        });
        return;
      }

      // Track demo mode exit
      trackEvent(DEMO_MODE_EVENTS.DEMO_MODE_EXITED, {
        businessId: userState.business?.id,
        primaryBusinessId: primaryBusiness.id,
        source: 'sidebar_connect_button',
      });

      // Use the same business switching logic as demo mode banner and account settings
      await switchBusiness({
        business: primaryBusiness,
        currentPath: location.pathname,
        currentClientId: clientState?.selectedClient?.id,
      });

      // Don't reset isExiting here - component will unmount during business switch
      // Keeping isExiting=true ensures loader stays visible until unmount
    } catch (error) {
      setIsExiting(false);
      toast({
        variant: 'destructive',
        title: t('demo-mode-unable-to-switch-business'),
        description: t('demo-mode-please-try-again-or-use-account-switcher'),
      });
    }
  }, [
    userState.business?.id,
    userState.businessOptions,
    switchBusiness,
    location.pathname,
    clientState?.selectedClient?.id,
    t,
  ]);

  const features = [
    {
      id: 'viewAnalytics',
      label: t('demo-mode-view-analytics-dashboard'),
      completed: progress.viewedAnalytics,
    },
    {
      id: 'createChart',
      label: t('demo-mode-create-custom-chart'),
      completed: progress.createdChart,
    },
    {
      id: 'createReport',
      label: t('demo-mode-generate-report'),
      completed: progress.createdReport,
    },
  ];

  // Show full-screen loader when exiting demo mode - show loader even if not in demo mode anymore
  if (isExiting) {
    return <BusinessSwitchLoader />;
  }

  // Don't show sidebar card if not in demo mode - check after loader check
  if (!userState.business?.is_demo_account) {
    return null;
  }

  return (
    <div className="relative w-full flex flex-col p-4 rounded-lg border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          {t('demo-mode-title')}
        </h3>
        {isComplete && <span className="text-base">🎉</span>}
      </div>

      {/* Progress Summary */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600">
            {isComplete ? t('demo-mode-complete') : t('demo-mode-progress')}
          </span>
          <span className="text-xs font-semibold text-gray-900">
            {progressCount}/3
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Checklist */}
      <div className="mb-3 space-y-2">
        {features.map(feature => (
          <div key={feature.id} className="flex items-center gap-2">
            <div className="flex-shrink-0">
              {feature.completed ? (
                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                  <i className="ti ti-check text-white text-[10px]" />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
              )}
            </div>
            <span
              className={cn(
                'text-xs flex-1',
                feature.completed
                  ? 'text-gray-400 line-through'
                  : 'text-gray-700'
              )}
            >
              {feature.label}
            </span>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      {!isComplete ? (
        <>
          <p className="text-xs text-gray-600 mb-3 leading-relaxed">
            {t('demo-mode-explore-description')}
          </p>
          <Button
            variant="contain"
            className="w-full justify-center gap-2 py-2 px-3 text-xs font-medium"
            onClick={handleExploreDashboard}
          >
            <i className="ti ti-chart-line text-sm" />
            {t('demo-mode-explore-analytics')}
          </Button>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-600 mb-3 leading-relaxed">
            {t('demo-mode-complete-description')}
          </p>
          <Button
            variant="contain"
            className={cn(
              'w-full justify-center gap-2 py-2 px-3 text-xs font-medium relative',
              isComplete && 'animate-spotlight'
            )}
            onClick={() => setShowExitConfirmation(true)}
            disabled={isExiting}
          >
            {isExiting ? (
              <>
                <Spinner size="sm" className="h-3 w-3" />
                <span>{t('demo-mode-switching')}</span>
              </>
            ) : (
              <>
                <i className="ti ti-plug text-sm" />
                {t('demo-mode-connect-your-data')}
              </>
            )}
          </Button>
        </>
      )}

      {/* Confetti */}
      <Confetti
        ref={confettiRef}
        className="absolute inset-0 top-auto pointer-events-none"
        manualstart
      />

      {/* Spotlight animation styles */}
      <style>
        {`
          @keyframes spotlight {
            0% {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
            }
            100% {
              box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
            }
          }
          .animate-spotlight {
            animation: spotlight 2s ease-out infinite;
          }
        `}
      </style>

      {/* Exit Demo Mode Confirmation Dialog */}
      <Dialog
        open={showExitConfirmation}
        onOpenChange={setShowExitConfirmation}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('demo-mode-exit-demo-mode')}</DialogTitle>
            <DialogDescription>
              {t('demo-mode-exit-demo-mode-description')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowExitConfirmation(false)}
              disabled={isExiting}
              className="w-full sm:w-auto"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleExitDemoMode}
              disabled={isExiting}
              className="w-full sm:w-auto gap-2"
            >
              {isExiting ? (
                <>
                  <Spinner size="sm" className="h-3 w-3" />
                  <span>{t('demo-mode-switching')}</span>
                </>
              ) : (
                <>
                  <i className="ti ti-arrow-back text-sm" />
                  <span>{t('demo-mode-exit-demo-mode')}</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoSidebarCard;
